/**
 * Conversation Compacting
 * Auto-compress long conversations to save 60-80% tokens
 */

const MAX_TOKENS = 100000; // Approximate max context
const COMPACT_THRESHOLD = 0.8; // Compact at 80% full
const KEEP_RECENT_MESSAGES = 10; // Always keep last N messages

export class ConversationCompactor {
  constructor(modelClient) {
    this.modelClient = modelClient;
  }

  /**
   * Estimate token count (rough approximation)
   */
  estimateTokens(text) {
    // Rough estimate: ~4 characters per token
    return Math.ceil(text.length / 4);
  }

  /**
   * Calculate total tokens in conversation
   */
  calculateConversationTokens(messages) {
    let total = 0;
    for (const msg of messages) {
      total += this.estimateTokens(msg.content);
    }
    return total;
  }

  /**
   * Check if conversation needs compacting
   */
  needsCompacting(messages) {
    const tokens = this.calculateConversationTokens(messages);
    return tokens > (MAX_TOKENS * COMPACT_THRESHOLD);
  }

  /**
   * Compact conversation
   */
  async compactConversation(messages) {
    if (!this.needsCompacting(messages)) {
      return {
        compacted: false,
        messages,
        reason: 'Below threshold'
      };
    }

    console.log('[Compactor] Starting compaction...');

    const totalTokens = this.calculateConversationTokens(messages);
    console.log(`[Compactor] Current tokens: ${totalTokens}`);

    // Split messages into: old (to compact) and recent (to keep)
    const recentMessages = messages.slice(-KEEP_RECENT_MESSAGES);
    const oldMessages = messages.slice(0, -KEEP_RECENT_MESSAGES);

    if (oldMessages.length === 0) {
      return {
        compacted: false,
        messages,
        reason: 'All messages are recent'
      };
    }

    // Create summary of old messages
    const summary = await this.summarizeMessages(oldMessages);

    // Create compacted conversation
    const compactedMessages = [
      {
        id: `summary-${Date.now()}`,
        role: 'system',
        content: `📝 **Conversation Summary** (${oldMessages.length} messages compressed):\n\n${summary}`,
        timestamp: Date.now(),
        isCompacted: true
      },
      ...recentMessages
    ];

    const newTokens = this.calculateConversationTokens(compactedMessages);
    const savedTokens = totalTokens - newTokens;
    const percentSaved = ((savedTokens / totalTokens) * 100).toFixed(1);

    console.log(`[Compactor] ✅ Compacted! Saved ${savedTokens} tokens (${percentSaved}%)`);

    return {
      compacted: true,
      messages: compactedMessages,
      stats: {
        originalMessages: messages.length,
        compactedMessages: compactedMessages.length,
        originalTokens: totalTokens,
        newTokens,
        savedTokens,
        percentSaved: parseFloat(percentSaved)
      }
    };
  }

  /**
   * Summarize messages using AI
   */
  async summarizeMessages(messages) {
    // Create manual summary for now (AI summarization can be added later)
    return this.createManualSummary(messages);
  }

  /**
   * Create manual summary
   */
  createManualSummary(messages) {
    const userMessages = messages.filter(m => m.role === 'user');
    const topics = userMessages.map(m => {
      const content = m.content.split('\n')[0];
      return content.length > 100 ? content.substring(0, 100) + '...' : content;
    });

    return `**Topics discussed** (${messages.length} messages):\n` +
           topics.map((t, i) => `${i + 1}. ${t}`).join('\n');
  }

  /**
   * Auto-compact if needed
   */
  async autoCompact(messages) {
    if (this.needsCompacting(messages)) {
      console.log('[Compactor] Auto-compacting conversation...');
      return await this.compactConversation(messages);
    }

    return {
      compacted: false,
      messages
    };
  }

  /**
   * Get compaction stats
   */
  getStats(messages) {
    const tokens = this.calculateConversationTokens(messages);
    const percentUsed = ((tokens / MAX_TOKENS) * 100).toFixed(1);
    const needsCompact = this.needsCompacting(messages);

    return {
      messages: messages.length,
      tokens,
      maxTokens: MAX_TOKENS,
      percentUsed: parseFloat(percentUsed),
      needsCompacting: needsCompact,
      remainingTokens: MAX_TOKENS - tokens
    };
  }
}

export default ConversationCompactor;
